import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface FormData {
  fullName: string;
  phone: string;
  email: string;
  serviceType: string;
  propertyLocation: string;
  message: string;
}

const serviceOptions = [
  'Automotive Window Tinting',
  'Residential Window Tinting',
  'Marine Window Tinting',
  'Paint Protection Film (PPF)',
  'Ceramic Coating',
  'Multiple Services',
];

export function ContactCTA() {
  const { ref, isVisible } = useScrollAnimation();
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    phone: '',
    email: '',
    serviceType: '',
    propertyLocation: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d73enscuf8iffoai1qe0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitStatus(res.ok ? 'success' : 'error');
      if (res.ok) {
        setFormData({ fullName: '', phone: '', email: '', serviceType: '', propertyLocation: '', message: '' });
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="quote" className="py-28 bg-[#F7F5F2] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#FF6B35]/6 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10" ref={ref}>
        {/* Section header */}
        <div className={`anim-hidden ${isVisible ? 'animate-fade-in-up' : ''} text-center mb-16`}>
          <span className="luxury-divider mx-auto" />
          <span className="text-xs font-bold uppercase tracking-[5px] text-[#C9A96E] mb-4 block">
            Get in Touch
          </span>
          <h2
            className="text-4xl sm:text-5xl font-bold text-[#0A0A0A] leading-tight tracking-tight"
            style={{ fontFamily: 'Sora, sans-serif' }}
          >
            Book Your Appointment Today
          </h2>
          <p className="text-gray-500 text-sm mt-4 max-w-xl mx-auto">
            Free estimates — no commitment. Most automotive jobs completed same day.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          {/* Left info panel */}
          <div className={`anim-hidden ${isVisible ? 'animate-fade-in-left delay-100' : ''} lg:col-span-2 space-y-5`}>
            {/* Offer card */}
            <div className="bg-[#0A0A0A] rounded-2xl p-7">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-[#FF6B35]/20 rounded-xl">
                  <i className="ri-gift-line text-[#FF6B35] text-xl" />
                </div>
                <span className="text-white text-sm font-bold" style={{ fontFamily: 'Sora, sans-serif' }}>
                  Limited Time Offer
                </span>
              </div>
              <p className="text-white/70 text-sm leading-relaxed">
                Tint <strong className="text-white">3 windows, get 1 FREE.</strong> Call or submit your quote to claim this exclusive deal before it expires.
              </p>
            </div>

            {/* Contact info */}
            <div className="bg-white rounded-2xl p-7 border border-gray-100 space-y-5">
              {[
                {
                  icon: 'ri-phone-fill',
                  label: 'Phone',
                  value: '(561) 275-9473',
                  href: 'tel:+15612759473',
                },
                {
                  icon: 'ri-map-pin-line',
                  label: 'Address',
                  value: '1257 SW Martin Hwy, Palm City, FL 34990',
                  href: undefined,
                },
                {
                  icon: 'ri-time-line',
                  label: 'Hours',
                  value: 'Mon–Fri 8AM–6PM · Sat 9AM–4PM',
                  href: undefined,
                },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#FF6B35]/10 rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-[#FF6B35] text-base`} />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-widest mb-0.5">{item.label}</div>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-sm font-bold text-[#0A0A0A] hover:text-[#FF6B35] transition-colors cursor-pointer"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <span className="text-sm font-semibold text-[#0A0A0A]">{item.value}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Call to action shortcut */}
            <a
              href="tel:+15612759473"
              className="flex items-center justify-center gap-3 bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white w-full py-4 rounded-2xl text-sm font-bold transition-all cursor-pointer"
            >
              <i className="ri-phone-fill text-[#FF6B35] text-lg" />
              Call Now for Same-Day Service
            </a>
          </div>

          {/* Right: Form */}
          <div className={`anim-hidden ${isVisible ? 'animate-fade-in-right delay-200' : ''} lg:col-span-3`}>
            <form
              id="quote-form"
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-white rounded-2xl p-8 border border-gray-100"
            >
              <h3
                className="text-xl font-bold text-[#0A0A0A] mb-6"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                Get a Fast Quote
              </h3>

              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="fullName" className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                      Full Name <span className="text-[#FF6B35]">*</span>
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 outline-none transition-all text-sm text-[#0A0A0A]"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                      Phone <span className="text-[#FF6B35]">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="(561) 555-0123"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 outline-none transition-all text-sm text-[#0A0A0A]"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                    Email Address <span className="text-[#FF6B35]">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 outline-none transition-all text-sm text-[#0A0A0A]"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="serviceType" className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                      Service Needed
                    </label>
                    <select
                      id="serviceType"
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 outline-none transition-all text-sm text-[#0A0A0A] bg-white cursor-pointer"
                    >
                      <option value="">Select a service...</option>
                      {serviceOptions.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="propertyLocation" className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                      Your City / Location
                    </label>
                    <input
                      type="text"
                      id="propertyLocation"
                      name="propertyLocation"
                      value={formData.propertyLocation}
                      onChange={handleChange}
                      placeholder="West Palm Beach, FL"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 outline-none transition-all text-sm text-[#0A0A0A]"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                    Tell Us More
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    maxLength={500}
                    placeholder="Vehicle type, number of windows, film preference..."
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 outline-none transition-all text-sm text-[#0A0A0A] resize-none"
                  />
                  <div className="text-xs text-gray-400 text-right mt-1">
                    {formData.message.length}/500
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#FF6B35] hover:bg-[#E55A2B] text-white py-4 rounded-xl text-sm font-bold tracking-wide transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Submit My Quote Request'}
                </button>

                {submitStatus === 'success' && (
                  <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl text-sm">
                    <i className="ri-checkbox-circle-line text-green-600" />
                    Request received! We&apos;ll be in touch within 1 business hour.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-sm">
                    <i className="ri-error-warning-line text-red-500" />
                    Something went wrong. Please call us at (561) 275-9473.
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
